import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactElement, ReactNode } from 'react';
import { buttonClassName, iconButtonClassName } from './buttonStyles';
import type { ButtonSize, ButtonVariant } from './buttonStyles';
import { cn } from './cn';
import { ChevronDown } from './icons';
import { Portal } from './Portal';
import { computePlacement } from './positioning';
import type { Placement, PopoverAlign } from './positioning';
import { Tooltip } from './Tooltip';

export interface MenuActionItem {
  type?: 'item';
  id: string;
  label: string;
  icon?: ReactNode;
  /** Right-aligned hint, e.g. `Ctrl+S`. Announced via `aria-keyshortcuts`. */
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuSeparatorItem {
  type: 'separator';
  id: string;
}

export type MenuItem = MenuActionItem | MenuSeparatorItem;

export interface MenuProps {
  items: readonly MenuItem[];
  /** Accessible name for the trigger, and its tooltip when icon-only. */
  label: string;
  /** Visible trigger text. Omit for an icon-only trigger. */
  triggerLabel?: string;
  triggerIcon?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  side?: 'top' | 'bottom';
  align?: PopoverAlign;
  disabled?: boolean;
  /** Hides the trailing chevron on a text trigger. */
  hideChevron?: boolean;
  className?: string;
  menuClassName?: string;
}

/** Milliseconds after which the typeahead buffer starts over. */
const TYPEAHEAD_RESET = 600;

function isAction(item: MenuItem): item is MenuActionItem {
  return item.type !== 'separator';
}

/**
 * Dropdown menu following the WAI-ARIA menu button pattern: roving DOM focus
 * across `menuitem`s, full arrow/Home/End/typeahead navigation, and focus
 * returned to the trigger on close.
 */
export function Menu({
  items,
  label,
  triggerLabel,
  triggerIcon,
  variant = 'secondary',
  size = 'md',
  side = 'bottom',
  align = 'start',
  disabled = false,
  hideChevron = false,
  className,
  menuClassName,
}: MenuProps): ReactElement {
  const triggerId = useId();
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const typeahead = useRef({ buffer: '', at: 0 });

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const findEdge = useCallback(
    (from: 'first' | 'last'): number => {
      if (from === 'first') {
        for (let index = 0; index < items.length; index += 1) {
          if (isAction(items[index])) return index;
        }
        return -1;
      }
      for (let index = items.length - 1; index >= 0; index -= 1) {
        if (isAction(items[index])) return index;
      }
      return -1;
    },
    [items],
  );

  const findNext = useCallback(
    (from: number, delta: number): number => {
      const count = items.length;
      if (count === 0) return -1;
      let index = from < 0 ? (delta > 0 ? -1 : 0) : from;
      for (let attempt = 0; attempt < count; attempt += 1) {
        index = (index + delta + count) % count;
        if (isAction(items[index])) return index;
      }
      return -1;
    },
    [items],
  );

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    setActiveIndex(-1);
    setPlacement(null);
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
  }, []);

  function openMenu(from: 'first' | 'last'): void {
    if (disabled) return;
    setActiveIndex(findEdge(from));
    setOpen(true);
  }

  // Roving focus: the active index owns DOM focus while the menu is open.
  useEffect(() => {
    if (!open) return;
    itemRefs.current[activeIndex]?.focus({ preventScroll: true });
  }, [open, activeIndex]);

  useLayoutEffect(() => {
    if (!open) return;
    function update(): void {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;
      setPlacement(
        computePlacement(
          trigger.getBoundingClientRect(),
          { width: menu.offsetWidth, height: menu.offsetHeight },
          { side, align, gap: 6, padding: 8 },
        ),
      );
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, side, align, items]);

  useEffect(() => {
    if (!open) return;
    function onOutside(event: Event): void {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (menuRef.current?.contains(target)) return;
      // The trigger's own click handler owns the toggle.
      if (triggerRef.current?.contains(target)) return;
      close(false);
    }
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('touchstart', onOutside, true);
    return () => {
      document.removeEventListener('mousedown', onOutside, true);
      document.removeEventListener('touchstart', onOutside, true);
    };
  }, [open, close]);

  function matchTypeahead(character: string): number {
    const now = Date.now();
    const state = typeahead.current;
    const lower = character.toLowerCase();
    state.buffer = now - state.at > TYPEAHEAD_RESET ? lower : state.buffer + lower;
    state.at = now;

    const count = items.length;
    // A repeated single character cycles; a longer buffer refines in place.
    const start = state.buffer.length > 1 ? Math.max(activeIndex, 0) : activeIndex + 1;
    for (let attempt = 0; attempt < count; attempt += 1) {
      const index = (((start + attempt) % count) + count) % count;
      const item = items[index];
      if (isAction(item) && item.label.toLowerCase().startsWith(state.buffer)) return index;
    }
    return -1;
  }

  function handleMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex(findNext(activeIndex, 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex(findNext(activeIndex, -1));
        return;
      case 'Home':
        event.preventDefault();
        setActiveIndex(findEdge('first'));
        return;
      case 'End':
        event.preventDefault();
        setActiveIndex(findEdge('last'));
        return;
      case 'Escape':
        event.preventDefault();
        // Stop an enclosing dialog from closing on the same key press.
        event.stopPropagation();
        close(true);
        return;
      case 'Tab':
        event.preventDefault();
        close(true);
        return;
      default:
        break;
    }

    if (event.key.length !== 1 || event.key === ' ') return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const match = matchTypeahead(event.key);
    if (match >= 0) {
      event.preventDefault();
      setActiveIndex(match);
    }
  }

  function handleTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openMenu('first');
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      openMenu('last');
    }
  }

  function selectItem(item: MenuActionItem): void {
    if (item.disabled) return;
    // Restore focus first so anything the action opens captures the trigger.
    close(true);
    item.onSelect();
  }

  const iconOnly = triggerLabel === undefined;
  const trigger = (
    <button
      ref={triggerRef}
      id={triggerId}
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      aria-label={iconOnly ? label : undefined}
      aria-disabled={disabled || undefined}
      onClick={() => {
        if (disabled) return;
        if (open) close(true);
        else openMenu('first');
      }}
      onKeyDown={handleTriggerKeyDown}
      className={
        iconOnly
          ? iconButtonClassName({ variant, compact: true, disabled, className })
          : buttonClassName({ variant, size, disabled, className })
      }
    >
      {triggerIcon && (
        <span aria-hidden="true" className="flex items-center [&>svg]:h-4 [&>svg]:w-4">
          {triggerIcon}
        </span>
      )}
      {triggerLabel}
      {!iconOnly && !hideChevron && (
        <ChevronDown aria-hidden="true" className="-mr-1 h-4 w-4 text-ink-faint" />
      )}
    </button>
  );

  return (
    <>
      {iconOnly ? (
        <Tooltip label={label} disabled={open}>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}

      {open && (
        <Portal>
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-labelledby={triggerId}
            tabIndex={-1}
            onKeyDown={handleMenuKeyDown}
            style={{ top: placement?.top ?? 0, left: placement?.left ?? 0 }}
            className={cn(
              'fs-scrollbar fixed z-[65] max-h-[min(24rem,80vh)] min-w-52 max-w-80 overflow-y-auto',
              'rounded-lg border border-border bg-surface-raised p-1 shadow-pop outline-none',
              'transition-opacity duration-100 motion-reduce:transition-none',
              placement ? 'opacity-100' : 'opacity-0',
              menuClassName,
            )}
          >
            {items.map((item, index) => {
              if (!isAction(item)) {
                return <div key={item.id} role="separator" className="my-1 h-px bg-border" />;
              }
              return (
                <button
                  key={item.id}
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  aria-disabled={item.disabled || undefined}
                  aria-keyshortcuts={item.shortcut}
                  onClick={() => selectItem(item)}
                  className={cn(
                    'flex w-full min-h-9 max-md:min-h-11 items-center gap-2.5 rounded-md px-2 py-1.5',
                    'text-left text-[13px] outline-none transition-colors duration-100',
                    'motion-reduce:transition-none',
                    item.disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'focus:bg-surface-sunken hover:bg-surface-sunken',
                    item.danger ? 'text-danger' : 'text-ink',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="flex w-4 shrink-0 items-center text-ink-faint [&>svg]:h-4 [&>svg]:w-4"
                  >
                    {item.icon}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.shortcut && (
                    <span
                      aria-hidden="true"
                      className="ml-4 shrink-0 font-mono text-[11px] text-ink-faint"
                    >
                      {item.shortcut}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </Portal>
      )}
    </>
  );
}
